import styled, { css } from 'styled-components';

import { TaggedText } from 'components/TaggedText';
import { simpleDateAtTime } from 'lib/date';
import Activity from 'models/Activity';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import NoteMenu from './NoteMenu';

// A mention email links to one note, and the tab scrolls it into view on arrival by id. Exported
// so the two sides cannot drift.
export const noteRowId = (activityId: string) => `activity-${activityId}`;

// The drawer's own padding (ClinicalReviewPanel's SlideoutBody). A marked note's tint bleeds out by
// exactly this much to reach the panel's edges, and the note list has to bleed with it or the row is
// clipped. Declared here, beside the rule that uses it, and imported by NotesTab so the two cannot
// drift apart — this file is the leaf, so the dependency runs the only way it can.
export const DRAWER_PADDING = 24;

/**
 * One note: author, timestamp, body, and — for the author only — a kebab.
 *
 * No facility line: the mock doesn't show one. The API sends created_by_group and the model
 * exposes `createdByGroup`, so adding it later is a frontend-only change.
 */
export default function NoteRow({
  note,
  workflowId,
  actionId,
  isAuthor,
  focused = false,
}: {
  note: Activity;
  workflowId: string;
  actionId: string;
  isAuthor: boolean;
  /** The note a mention link pointed at — marked so the reader can tell which one wanted them. */
  focused?: boolean;
}) {
  return (
    <Row id={noteRowId(note.id)} data-testid='note-row' $focused={focused} data-focused={focused || undefined}>
      <Header>
        <Author data-testid='note-author'>{note.createdBy?.fullName}</Author>
        {note.createdAt && <Timestamp>{simpleDateAtTime(note.createdAt)}</Timestamp>}
        {isAuthor && (
          <MenuCell>
            <NoteMenu note={note} workflowId={workflowId} actionId={actionId} />
          </MenuCell>
        )}
      </Header>
      <Body data-testid='note-body'>
        <TaggedText text={note.data.text ?? ''} $archived={!!note.archived} />
      </Body>
    </Row>
  );
}

const Row = styled.div<{ $focused?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;

  /* Full-bleed to the drawer's edges — the list bleeds with it (NotesTab's List) — so a marked note
     reads as a band across the panel rather than a box floating in the thread. The matching padding
     puts the text back exactly where it was. Every row carries this, not just the focused one, so
     marking a note changes colour and nothing else: no reflow, and nothing to animate but the tint.
     Uniform on the first row too — the gap under the composer is the list's margin, not a zeroed
     padding-top here, which is what used to leave a marked first note's tint flush to its name. */
  margin: 0 -${DRAWER_PADDING}px;
  padding: 20px ${DRAWER_PADDING}px;

  /* Declared on the base, not inside the focused block, so the highlight animates OUT as well as in
     — NotesTab drops the flag after a couple of seconds and this is what makes that a fade rather
     than a snap. */
  transition: background-color 500ms ease-out;

  /* The divider stays at the thread's own width rather than following the bleed, so the thread
     looks exactly as it did. A pseudo-element for that reason — a border would run the full width
     with the row.

     Every row declares it and the first row hides it, rather than the sibling form: a
     self-reference only resolves to the stable component class when the selector both starts and
     ends with it, so a trailing ::before compiles it to the row's *dynamic* class instead — which
     differs between a focused row and its neighbours, leaving the rule matching neither. */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: ${DRAWER_PADDING}px;
    right: ${DRAWER_PADDING}px;
    border-top: 1px solid ${colors.black10};
    transition: border-top-color 500ms ease-out;
  }

  /* Nothing above the first note to divide it from. */
  &:first-child::before {
    border-top-color: transparent;
  }

  /* Arrived from a mention link: tint the note that asked for the reader, so a thread of
     near-identical rows says which one. A tinted band and nothing else — no border, no outline, no
     box-model change. */
  ${({ $focused }) =>
    $focused &&
    css`
      /* Doubled selectors, so these beat the divider rules above rather than depending on
         injection order. */
      /* Same value as the mention chip in the body (blue10 here, --primary-blue-10 there), so the
         chip reads as part of the band rather than against it. Tried at blue5 to separate the two;
         the band lost too much presence for the two seconds it is on screen. */
      && {
        background-color: ${colors.blue10};
      }

      /* The band owns this row's top edge; the divider would read as a line across the tint. Hidden
         by colour, so it fades back in with the band instead of snapping. */
      &&::before {
        border-top-color: transparent;
      }
    `}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Author = styled(BodySmallBold)`
  font-size: 14px;
  color: ${colors.black100};
`;

const Timestamp = styled(BodySmall)`
  font-size: 12px;
  color: ${colors.black50};
`;

const MenuCell = styled.div`
  margin-left: auto;
  position: relative;
`;

// TaggedText already strikes its own text through when archived, so this wrapper only owns the
// type scale — striking here too would be the same rule in two places.
const Body = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: ${colors.black100};

  /* TaggedText wraps its output in its own BodySmall, which carries the shared type scale.
     Inherit instead, so the row keeps the 14/22 the mock asks for. */
  & > * {
    font-size: inherit;
    line-height: inherit;
    color: inherit;
  }
`;
